import { AgentSearchService } from 'ag-common-svc/public-api';
import { Component, Input, OnInit } from '@angular/core';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import { AgentPermission, AgentPermissionKeys } from 'ag-common-lib/lib/models/utils/agent-permission.model';
import { ActiveLookup, Agent, AgentKeys, LookupKeys, Role, ChangeSourceType } from 'ag-common-lib/public-api';
import { AgentPermissionService } from 'ag-common-svc/lib/services/agent-permission.service';
import { AgentService, AuthService } from 'ag-common-svc/public-api';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { Observable, filter, firstValueFrom, from, map, of, shareReplay, switchMap, take } from 'rxjs';

@Component({
  selector: 'ag-crm-permissions',
  templateUrl: './permissions.component.html',
  styleUrls: ['./permissions.component.scss'],
})
export class PermissionsComponent implements OnInit {
  @Input() agent: Agent;
  @Input() sourceType: ChangeSourceType | null = null;

  @Input() canEdit = true;
  @Input() canDelete = true;
  @Input() canCreate = true;

  public readonly LookupKeys = LookupKeys;
  public readonly AgentKeys = AgentKeys;
  public readonly agentsSearchExpr = [
    AgentKeys.p_agent_first_name,
    AgentKeys.p_agent_last_name,
    AgentKeys.p_email,
    LookupKeys.description,
  ];
  public agentToAdd: (Partial<ActiveLookup> & Partial<Agent>) | null = null;
  public agentsDataSource$: Observable<DataSource | object>;
  public assignedAgentsDataSource$: Observable<AgentPermission[]>;

  constructor(
    public agentPermissionService: AgentPermissionService,
    public authService: AuthService,
    public agentService: AgentService,
    private agentSearchService: AgentSearchService,
  ) {}

  ngOnInit(): void {
    this.agentsDataSource$ = this.authService.loggedInAgent$.pipe(
      filter(Boolean),
      switchMap((loggedInAgent: Agent) => {
        const roles = loggedInAgent?.[AgentKeys.role] ?? [];
        const isPortalAdmin = roles.some(role => role === Role.PORTAL_ADMIN);

        if (isPortalAdmin) {
          return of(this.agentService.agentsDataSource);
        }

        if (!loggedInAgent?.[AgentKeys.p_mga_id]) {
          return of(this.createAgentsLookupDataSource([]));
        }

        return from(
          this.agentSearchService.search({
            filter: [AgentKeys.p_mga_id, '=', loggedInAgent.p_mga_id],
            sort: [{ selector: AgentKeys.p_agent_last_name, desc: false }],
          }),
        ).pipe(
          map(response => response?.data ?? []),
          take(1),
          map(agents => this.createAgentsLookupDataSource(Array.isArray(agents) ? agents : [])),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.assignedAgentsDataSource$ = this.agentPermissionService.getAgentPermissionsByOwnerId(
      this.agent[BaseModelKeys.dbId],
      AgentPermissionKeys.ownerName,
    );
  }

  agentDisplayExpr = (item: Partial<ActiveLookup> & Partial<Agent>): string => {
    if (!item) {
      return '';
    }

    const name =
      item[LookupKeys.description] ??
      [item[AgentKeys.p_agent_first_name], item[AgentKeys.p_agent_last_name]].filter(Boolean).join(' ');
    const email = item[AgentKeys.p_email];

    if (!email) {
      return name ?? '';
    }

    return `${name} (${email})`;
  };

  onAgentToAddSelectionChanged(e: {
    selectedItem?: Partial<ActiveLookup> & Partial<Agent>;
  }) {
    this.agentToAdd = e?.selectedItem ?? null;
  }

  async onAddAgentToPermission() {
    if (!this.agentToAdd?.[LookupKeys.value]) {
      return;
    }

    const agentEmail = await firstValueFrom(this.authService.loggedInAgent$.pipe(map(agent => agent?.p_email)));
    const permission: AgentPermission = Object.assign({}, new AgentPermission(), {
      created_by: agentEmail,
      granted_to_id: this.agentToAdd[LookupKeys.value],
      granted_to_name: this.agentDisplayExpr(this.agentToAdd),
      owner_id: this.agent[BaseModelKeys.dbId],
      owner_name: this.agent[AgentKeys.p_agent_name],
      [BaseModelKeys.actionName]: this.sourceType,
    });

    this.agentPermissionService.create(permission);
    this.agentToAdd = null;
  }

  onRemoveAgentFromPermissionsList(e) {
    this.agentPermissionService.delete(e.itemData.dbId, this.sourceType);
  }

  private createAgentsLookupDataSource(agents: Agent[]): DataSource {
    const items = agents.map(agent => {
      const description = [agent?.[AgentKeys.p_agent_first_name], agent?.[AgentKeys.p_agent_last_name]]
        .filter(Boolean)
        .join(' ');

      if (!description) {
        console.log('agent', agent?.[BaseModelKeys.dbId]);
        console.log('agent', agent);
      }

      return {
        [LookupKeys.value]: agent?.[BaseModelKeys.dbId],
        [LookupKeys.description]: description,
        [AgentKeys.p_agent_first_name]: agent?.[AgentKeys.p_agent_first_name],
        [AgentKeys.p_agent_last_name]: agent?.[AgentKeys.p_agent_last_name],
        [AgentKeys.p_email]: agent?.[AgentKeys.p_email],
      };
    });

    return new DataSource({
      paginate: true,
      pageSize: 15,
      sort: [{ selector: LookupKeys.description, desc: false }],
      store: new ArrayStore({
        key: LookupKeys.value,
        data: items,
      }),
    });
  }
}
